import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import ContactEnrichPanel from '../components/ContactEnrichPanel';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';
import { toast } from '../utils/toast';

const STATUS_OPTIONS = ['New', 'Contacted', 'Qualified', 'Disqualified'];
const SOURCE_OPTIONS = ['Website', 'Referral', 'LinkedIn', 'Cold Call', 'Conference', 'Partner', 'Other'];
const SERVICE_TYPES = [
  'SAP Implementation',
  'SAP AMS',
  'SAP Migration',
  'IT Staffing',
  'Cloud Services',
  'Consulting',
  'Other'
];

const STATUS_BADGES = {
  New: 'badge-blue',
  Contacted: 'badge-yellow',
  Qualified: 'badge-green',
  Disqualified: 'badge-red'
};

const EMPTY_LEAD = {
  company_name: '', contact_name: '', contact_email: '', contact_phone: '',
  source: '', status: 'New', service_type: '', estimated_value: '', currency: 'INR',
  description: '', assigned_to: '',
  vertical: '', industry: '', sub_vertical: '', legal_entity: '',
  company_size: '', management_type: '',
  // Enrichment-populated. job_title is "Designation - Departments";
  // linkedin_url is a full https URL — rendered as a clickable link in
  // read-only mode.
  job_title: '', linkedin_url: ''
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_LEAD });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [allLookups, setAllLookups] = useState([]);
  // Enrichment side panel — same component handles every provider (Lusha,
  // RocketReach, …). `enrichProvider` selects which one is open. null = closed.
  const [enrichProvider, setEnrichProvider] = useState(null);
  const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();

  const fetchLeads = useCallback(async () => {
    try {
      const res = await apiFetch('/api/crm/leads');
      if (res.ok) {
        const data = await res.json();
        setLeads(data.data || data.leads || data || []);
      }
    } catch (err) { /* handled by apiFetch */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    async function loadLookups() {
      try {
        const res = await apiFetch('/api/metadata/lookups');
        if (res.ok) { const d = await res.json(); setAllLookups(d.rows || []); }
      } catch (e) { /* ignore */ }
    }
    loadLookups();
  }, []);

  function openCreate() {
    setEditingLead(null);
    setForm({ ...EMPTY_LEAD });
    setErrors({});
    resetDirty();
    setModalOpen(true);
  }

  function openEdit(lead) {
    setEditingLead(lead);
    setForm({
      company_name: lead.company_name || '',
      contact_name: lead.contact_name || '',
      contact_email: lead.email || '',
      contact_phone: lead.phone || '',
      source: lead.source || '',
      status: lead.status || 'New',
      service_type: lead.service_type || '',
      estimated_value: lead.estimated_value || '',
      description: lead.notes || '',
      assigned_to: lead.assigned_to || '',
      vertical: lead.vertical || '',
      industry: lead.industry || '',
      sub_vertical: lead.sub_vertical || '',
      legal_entity: lead.legal_entity || '',
      company_size: lead.company_size || '',
      management_type: lead.management_type || '',
      currency: lead.currency || 'INR',
      job_title: lead.job_title || '',
      linkedin_url: lead.linkedin_url || '',
    });
    setErrors({});
    resetDirty();
    setModalOpen(true);
  }

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    markDirty();
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  }

  function validate() {
    const errs = {};
    if (!form.company_name.trim()) errs.company_name = 'Company name is required';
    if (!form.contact_name.trim()) errs.contact_name = 'Contact name is required';
    if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
      errs.contact_email = 'Invalid email address';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);

    // Backend expects camelCase field names
    const body = {
      companyName: form.company_name,
      contactName: form.contact_name,
      email: form.contact_email || null,
      phone: form.contact_phone || null,
      source: form.source || null,
      status: form.status || 'New',
      serviceType: form.service_type || null,
      estimatedValue: form.estimated_value ? parseFloat(form.estimated_value) : null,
      notes: form.description || null,
      assignedTo: form.assigned_to || null,
      vertical: form.vertical || null,
      industry: form.industry || null,
      subVertical: form.sub_vertical || null,
      legalEntity: form.legal_entity || null,
      companySize: form.company_size || null,
      managementType: form.management_type || null,
      currency: form.currency,
      jobTitle:    form.job_title || null,
      linkedinUrl: form.linkedin_url || null,
    };

    try {
      const url = editingLead ? `/api/crm/leads/${editingLead.id}` : '/api/crm/leads';
      const method = editingLead ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });

      if (res.ok) {
        resetDirty();
        setModalOpen(false);
        fetchLeads();
      } else {
        const data = await res.json();
        setErrors({ _general: data.error || data.message || 'Failed to save lead' });
      }
    } catch (err) {
      setErrors({ _general: 'Network error. Please try again.' });
    }
    setSaving(false);
  }

  async function handleConvert(lead) {
    // Confirm with a positive (non-danger) prompt and a "Convert" button —
    // not "Delete". The earlier flow reused the danger-default which made
    // the button red and labelled it Delete, which scared users off.
    const ok = await toast.confirm({
      title: 'Convert lead to opportunity?',
      body: `"${lead.company_name}" will open in a new Opportunity form pre-filled from this lead. You can edit fields before saving — nothing is created until you click Save.`,
      confirmLabel: 'Convert',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;

    // Stash a prefill snapshot — Opportunities.jsx picks this up on mount,
    // opens its New form, populates the fields, and clears the storage so
    // a refresh doesn't keep re-applying the prefill.
    try {
      sessionStorage.setItem('bizsuite:opportunityPrefill', JSON.stringify({
        leadId: lead.id,
        name: `${lead.company_name || ''} Opportunity`.trim(),
        company_name: lead.company_name || '',
        contact_name: lead.contact_name || '',
        contact_email: lead.email || '',
        contact_phone: lead.phone || '',
        service_type: lead.service_type || '',
        estimated_value: lead.estimated_value || '',
        currency: lead.currency || 'INR',
        description: lead.notes || '',
        // Carry the enrichment fields too so the new opportunity inherits
        // the LinkedIn URL and "Designation - Departments" string.
        linkedin_url: lead.linkedin_url || '',
        job_title: lead.job_title || '',
      }));
    } catch (_) { /* sessionStorage may be disabled — convert still works, just without prefill */ }

    // Hash-router navigate. The Opportunities page reads the prefill on mount.
    window.location.hash = '/opportunities';
  }

  async function handleDelete(lead) {
    if (!(await toast.confirm({ body: `Delete lead "${lead.company_name}"?`, danger: true }))) return;
    try {
      await apiFetch(`/api/crm/leads/${lead.id}`, { method: 'DELETE' });
      fetchLeads();
    } catch (err) { /* ignore */ }
  }

  const columns = [
    { key: 'company_name', label: 'Company', sortable: true },
    { key: 'contact_name', label: 'Contact', sortable: true },
    { key: 'vertical', label: 'Vertical', sortable: true, render: (v) => v || '—' },
    { key: 'industry', label: 'Industry', sortable: true, render: (v) => v || '—' },
    { key: 'source', label: 'Source', sortable: true },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (val) => <span className={`badge ${STATUS_BADGES[val] || 'badge-gray'}`}>{val || 'New'}</span>
    },
    {
      key: 'estimated_value', label: 'Est. Value', sortable: true, type: 'number',
      render: (val) => val ? formatCurrency(val) : '—'
    },
    {
      key: 'created_at', label: 'Created', sortable: true, type: 'date',
      render: (val) => formatDate(val)
    }
  ];

  const actions = [
    { label: 'Edit', icon: 'edit', onClick: openEdit },
    { label: 'Convert', icon: 'convert', onClick: handleConvert },
    { label: 'Delete', icon: 'delete', onClick: handleDelete }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, marginTop: -40 }}>
        <button className="btn btn-primary" onClick={openCreate}>
          + New Lead
        </button>
      </div>

      <DataTable
        columns={columns}
        data={leads}
        actions={actions}
        loading={loading}
        emptyMessage="No leads found. Create your first lead to get started."
        emptyIcon={'\u{1F4CB}'}
      />

      <Modal
        title={editingLead ? 'Edit Lead' : 'New Lead'}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="lg"
        dirty={dirty}
      >
        {errors._general && (
          <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            {errors._general}
          </div>
        )}

        {(() => {
          const verticals = allLookups.filter(l => l.category === 'vertical' && l.status === 'Active');
          const selectedVerticalId = verticals.find(v => v.name === form.vertical)?.id;
          const industries = allLookups.filter(l => l.category === 'industry' && l.status === 'Active' && (!selectedVerticalId || l.parent_id === selectedVerticalId));
          const selectedIndustryId = industries.find(i => i.name === form.industry)?.id;
          const subVerticals = allLookups.filter(l => l.category === 'sub_vertical' && l.status === 'Active' && (!selectedIndustryId || l.parent_id === selectedIndustryId));
          const legalEntities = allLookups.filter(l => l.category === 'legal_entity' && l.status === 'Active');
          const sizes = allLookups.filter(l => l.category === 'size' && l.status === 'Active');
          const mgmtTypes = allLookups.filter(l => l.category === 'management_type' && l.status === 'Active');
          return (<>
            <div className="form-row">
              <FormField label="Company Name" name="company_name" value={form.company_name} onChange={handleField} error={errors.company_name} required />
              <FormField label="Contact Name" name="contact_name" value={form.contact_name} onChange={handleField} error={errors.contact_name} required />
            </div>
            <div className="form-row">
              <FormField label="Contact Email" name="contact_email" type="email" value={form.contact_email} onChange={handleField} error={errors.contact_email} />
              <FormField label="Contact Phone" name="contact_phone" value={form.contact_phone} onChange={handleField} />
            </div>
            <div className="form-row">
              {/* Job Title — combined "Designation - Departments" string. The
                  ContactEnrichPanel formats this when applying enrichment,
                  but the field stays editable as free text afterwards. */}
              <FormField label="Job Title (Designation - Department)" name="job_title"
                value={form.job_title} onChange={handleField}
                placeholder="e.g. VP of Sales - Sales" />
              {/* LinkedIn URL — rendered as a clickable hyperlink in the
                  read-only summary (see LinkedInPreview below). The form
                  itself stays a plain text input so users can paste/edit. */}
              <FormField label="LinkedIn URL" name="linkedin_url"
                value={form.linkedin_url} onChange={handleField}
                placeholder="https://www.linkedin.com/in/..." />
            </div>
            {/* Live preview link — appears under the form when linkedin_url
                is a valid URL. Click-through opens the profile in a new tab. */}
            {form.linkedin_url && /^https?:\/\//i.test(form.linkedin_url) && (
              <div style={{ margin: '-8px 0 14px', fontSize: 12 }}>
                <a href={form.linkedin_url} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#6FE6FF', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14ZM8.339 18.337v-8.59H5.667v8.59h2.672ZM7.003 8.574a1.55 1.55 0 1 0 0-3.097 1.55 1.55 0 0 0 0 3.097Zm11.335 9.763v-4.926c0-2.475-1.32-3.626-3.083-3.626-1.422 0-2.06.782-2.413 1.331v-1.143H10.17c.034.752 0 8.59 0 8.59h2.672v-4.797c0-.241.018-.481.087-.654.19-.481.628-.978 1.36-.978.96 0 1.345.732 1.345 1.806v4.624h2.704Z"/></svg>
                  Open profile
                </a>
              </div>
            )}
            {/* Enrichment buttons — one per provider. Disabled until the user
                types a company name so we have something to query against. */}
            <div style={{ margin: '4px 0 14px', display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <EnrichButton
                label="Check Lusha"
                disabled={!form.company_name?.trim()}
                onClick={() => setEnrichProvider('lusha')}
                title={form.company_name?.trim() ? 'Find contact details via Lusha' : 'Enter a company name first'}
              />
              <EnrichButton
                label="Check RocketReach"
                disabled={!form.company_name?.trim()}
                onClick={() => setEnrichProvider('rocketreach')}
                title={form.company_name?.trim() ? 'Find contact details via RocketReach' : 'Enter a company name first'}
              />
            </div>
            <div className="form-row">
              <FormField label="Vertical" name="vertical" type="select" value={form.vertical}
                onChange={(n, v) => { handleField(n, v); setForm(prev => ({ ...prev, vertical: v, industry: '', sub_vertical: '' })); }}
                options={verticals.map(v => v.name)} />
              <FormField label="Industry" name="industry" type="select" value={form.industry}
                onChange={(n, v) => { handleField(n, v); setForm(prev => ({ ...prev, industry: v, sub_vertical: '' })); }}
                options={industries.map(i => i.name)} />
            </div>
            <div className="form-row">
              <FormField label="Sub-Vertical" name="sub_vertical" type="select" value={form.sub_vertical} onChange={handleField} options={subVerticals.map(s => s.name)} />
              <FormField label="Legal Entity" name="legal_entity" type="select" value={form.legal_entity} onChange={handleField} options={legalEntities.map(l => l.name)} />
            </div>
            <div className="form-row">
              <FormField label="Size" name="company_size" type="select" value={form.company_size} onChange={handleField} options={sizes.map(s => s.name)} />
              <FormField label="Management Type" name="management_type" type="select" value={form.management_type} onChange={handleField} options={mgmtTypes.map(m => m.name)} />
            </div>
            <div className="form-row">
              <FormField label="Source" name="source" type="select" value={form.source} onChange={handleField} options={SOURCE_OPTIONS} />
              <FormField label="Status" name="status" type="select" value={form.status} onChange={handleField} options={STATUS_OPTIONS} />
            </div>
            <div className="form-row">
              <FormField label="Service Type" name="service_type" type="select" value={form.service_type} onChange={handleField} options={SERVICE_TYPES} />
              <FormField label="Estimated Value" name="estimated_value" type="number" value={form.estimated_value} onChange={handleField} min="0" step="1000" />
            </div>
            <div className="form-row">
              <FormField label="Currency" name="currency" type="select" value={form.currency} onChange={handleField} options={['INR', 'USD', 'EUR', 'GBP', 'SEK', 'NOK', 'AED', 'SGD']} />
            </div>
            <FormField label="Description" name="description" type="textarea" value={form.description} onChange={handleField} />
          </>);
        })()}

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (editingLead ? 'Update Lead' : 'Create Lead')}
          </button>
        </div>
      </Modal>

      {/* Provider-agnostic enrichment side modal. The same component handles
          every provider — `provider` prop selects which API endpoints to hit.
          Apply path merges patch back into the Lead form via setForm; if the
          user already populated contact_name/email/phone, the panel opens its
          OverwriteConfirmDialog with side-by-side diff before applying. */}
      <ContactEnrichPanel
        open={!!enrichProvider}
        provider={enrichProvider}
        onClose={() => setEnrichProvider(null)}
        lead={form}
        onApply={(patch) => {
          setForm(prev => ({ ...prev, ...patch }));
          markDirty();
        }}
      />
    </div>
  );
}

// Modern outlined button used by both Check Lusha + Check RocketReach.
// Pulled out so the two buttons stay visually consistent.
function EnrichButton({ label, onClick, disabled, title }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{
        padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: '1px solid rgba(0,224,255,0.40)',
        background: disabled ? 'rgba(255,255,255,0.04)' : 'rgba(0,224,255,0.10)',
        color: disabled ? '#64748B' : '#6FE6FF',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
      {label}
    </button>
  );
}
